import { useEffect, useRef, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { FiCheckCircle, FiXCircle, FiLoader, FiMail, FiShield } from 'react-icons/fi';
import { checkVerificationToken, verifyEmail, resendVerification } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import styles from './VerifyEmail.module.css';

const VerifyEmail = () => {
  const { token } = useParams();
  const { user, markEmailVerified, refreshUser } = useAuth();
  const [status, setStatus] = useState('loading'); // loading | confirm | confirming | success | error
  const [resendLoading, setResendLoading] = useState(false);
  const [resendMsg, setResendMsg] = useState('');
  const checked = useRef(false);

  useEffect(() => {
    if (!token || checked.current) return;
    checked.current = true;
    checkVerificationToken(token)
      .then((res) => setStatus(res.valid ? 'confirm' : 'error'))
      .catch(() => setStatus('error'));
  }, [token]);

  const handleConfirm = () => {
    setStatus('confirming');
    verifyEmail(token)
      .then(() => {
        setStatus('success');
        if (user) {
          markEmailVerified();
          refreshUser().catch(() => {});
        }
      })
      .catch(() => setStatus('error'));
  };

  const handleResend = async () => {
    setResendLoading(true);
    setResendMsg('');
    try {
      await resendVerification();
      setResendMsg('A new verification email has been sent.');
    } catch (err) {
      setResendMsg(err.response?.data?.message || 'Failed to resend. Please try again.');
    } finally {
      setResendLoading(false);
    }
  };

  return (
    <div className={styles.page}>
      <div className={styles.card}>
        {status === 'loading' && (
          <>
            <FiLoader className={styles.iconSpin} size={48} />
            <h2 className={styles.title}>Checking your link…</h2>
          </>
        )}

        {status === 'confirm' && (
          <>
            <FiShield size={48} />
            <h2 className={styles.title}>Confirm your email</h2>
            <p className={styles.desc}>Click below to verify your email address.</p>
            <button className={styles.btn} onClick={handleConfirm}>Verify email</button>
          </>
        )}

        {status === 'confirming' && (
          <>
            <FiLoader className={styles.iconSpin} size={48} />
            <h2 className={styles.title}>Verifying your email…</h2>
          </>
        )}

        {status === 'success' && (
          <>
            <FiCheckCircle className={styles.iconSuccess} size={48} />
            <h2 className={styles.title}>Email verified!</h2>
            <p className={styles.desc}>Your email address has been successfully verified. You can now use all platform features.</p>
            <Link to="/dashboard" className={styles.btn}>Go to Dashboard</Link>
          </>
        )}

        {status === 'error' && (
          <>
            <FiXCircle className={styles.iconError} size={48} />
            <h2 className={styles.title}>Verification failed</h2>
            <p className={styles.desc}>This verification link is invalid or has expired (links expire after 24 hours).</p>
            {user && !user.isEmailVerified && (
              <div className={styles.resendBox}>
                <button className={styles.btn} onClick={handleResend} disabled={resendLoading}>
                  <FiMail size={15} />
                  {resendLoading ? 'Sending…' : 'Resend verification email'}
                </button>
                {resendMsg && <p className={styles.resendMsg}>{resendMsg}</p>}
              </div>
            )}
            <Link to="/dashboard" className={styles.linkBack}>← Back to Dashboard</Link>
          </>
        )}
      </div>
    </div>
  );
};

export default VerifyEmail;
